import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  InputActionMeta,
  MultiValueRemoveProps,
} from "react-select";
import { Option } from "../../interface/Option";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";

interface ListOfSpecialtyProps {
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any;
  value?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "list-of-specialty";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};


type SpecialtyOption = Option & {
  __inactive?: boolean;
  is_active?: boolean | null;
  status?: string;
  dbId?: number;
};

const statusOf = (item: any): boolean | null => {
  if (!item || typeof item !== "object") return null;
  const bool = (v: any): boolean | null => {
    if (v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true") return true;
    if (v === false || v === 0 || v === "0" || String(v).toLowerCase() === "false") return false;
    return null;
  };
  if (bool(item.is_deleted ?? item.isDeleted) === true) return false;
  const status = String(item.status ?? item.Status ?? "").trim().toLowerCase();
  const active = bool(item.is_active ?? item.isActive ?? item.active);
  if (active === false || status === "inactive" || status === "disabled") return false;
  if (active === true || status === "active" || status === "enabled") return true;
  return null;
};
const idOf = (item: any) => String(item?.value ?? item?.dbId ?? item?.id ?? "");
const locked = (item: SpecialtyOption) => item.__inactive === true || statusOf(item) === false;

const fetchAllSpecialties = async (): Promise<Option[]> => {

  return SpecialtyAxiosInstance.get("getSpecialty")
    .then((response) => {
      const rawResult =
        response.data?.result ??
        response.data?.activities ??
        response.data?.data ??
        [];

      const data = Array.isArray(rawResult)
        ? rawResult
        : Array.isArray(rawResult?.data)
          ? rawResult.data
          : [];

      const formattedOptions: SpecialtyOption[] = data
        .map((item: any) => {
          // SQL database ID — this stays as the selected value for payload.
          const sqlId =
            item?.id ??
            item?.specialty_id ??
            item?.Specialty_id;

          // Original/business Specialty code — display only.
          const specialtyCode = String(
            item?.speciality_code ??
              item?.specialty_code ??
              item?.Speciality_code ??
              item?.Specialty_code ??
              ""
          ).trim();

          const specialtyName = String(
            item?.name ??
              item?.Specialty_name ??
              item?.specialty_name ??
              item?.speciality_name ??
              ""
          ).trim();

          if (
            sqlId === null ||
            sqlId === undefined ||
            !specialtyName
          ) {
            console.warn("Invalid specialty item:", item);
            return null;
          }

          return {
            __inactive: statusOf(item) === false,
            is_active: statusOf(item),
            status: item?.status ?? item?.Status,
            // SQL ID for payload
            value: String(sqlId),

            // Business/original code for visible label
            label: specialtyCode
              ? `(${specialtyCode}) ${specialtyName}`
              : specialtyName,

            // Preserve IDs/metadata for Edit/View consumers.
            dbId: Number(sqlId),
            specialtyDbId: Number(sqlId),
            speciality_code: specialtyCode,
            specialty_code: specialtyCode,
            name: specialtyName,
          } as Option;
        })
        .filter((item: Option | null): item is Option => item !== null);

      return formattedOptions;
    });
};

const ListOfSpecialty = ({
  onChange,
  clear,
  defaultValue,
  value,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListOfSpecialtyProps) => {
  const [options, setOptions] = useState<SpecialtyOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SpecialtyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [allOptions, setAllOptions] = useState<SpecialtyOption[]>([]);

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadAllOptions = useCallback(async () => {
    setLoading(true);

    try {
      const formattedOptions = await fetchAllSpecialties();
      setAllOptions(formattedOptions);
      setOptions(formattedOptions.filter(o => statusOf(o) === true));
    } catch (error) {
      console.error("Error loading specialties:", error);
      setAllOptions([]);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAllOptions();
  }, [loadAllOptions]);

  // Local filtering only — no extra API request while typing.
  useEffect(() => {
    const query = inputValue.trim().toLowerCase();

    if (query.length === 0) {
      setOptions(allOptions.filter(o => statusOf(o) === true));
      return;
    }

    // Name-only filtering. Remove the displayed "(code)" prefix before matching.
    const filtered = allOptions.filter((option) => {
      if (statusOf(option) !== true) return false;
      const specialtyName = option.label.replace(/^\([^)]*\)\s*/, "");
      return specialtyName.toLowerCase().includes(query);
    });

    setOptions(filtered);
  }, [inputValue, allOptions]);

  // Resolve linked specialties against the full backend catalogue by SQL ID.
  // Inactive selections are retained even though they are excluded from the menu.
  useEffect(() => {
    if (clear) return;
    const incoming = value ?? defaultValue;
    if (incoming === undefined) return;
    const entries = Array.isArray(incoming) ? incoming : incoming ? [incoming] : [];
    setSelectedOptions(previous => entries.map((entry: any) => {
      const id = idOf(entry);
      const current = allOptions.find(o => idOf(o) === id);
      const prior = previous.find(o => idOf(o) === id);
      const state = current ? statusOf(current) : statusOf(entry) ?? (prior ? statusOf(prior) : null);
      return {
        ...(prior || {}), ...entry, ...(current || {}),
        value: id, label: entry?.label ?? current?.label ?? prior?.label ?? id,
        is_active: state, __inactive: state === false,
      } as SpecialtyOption;
    }));
  }, [value, defaultValue, allOptions, clear]);

  const handleChange = (selected: MultiValue<SpecialtyOption>) => {
    const proposed = [...selected];
    const retained = selectedOptions.filter(o =>
      locked(o) && !proposed.some(p => idOf(p) === idOf(o)));
    const updated = [...proposed, ...retained];
    setSelectedOptions(updated);
    onChange(updated);
  };

  useEffect(() => {
    if (clear) setSelectedOptions(previous => previous.filter(locked));
  }, [clear]);

  const handleInputChange = (
    nextValue: string,
    actionMeta: InputActionMeta
  ): string => {
    if (actionMeta.action === "input-change") {
      setInputValue(nextValue);
    }

    return nextValue;
  };

  const customStyles: StylesConfig<SpecialtyOption, true> = {
    multiValue: (styles, props) => ({
      ...styles,
      backgroundColor: locked(props.data) ? "#d1d5db" : "#007BFF",
      border: locked(props.data) ? "1px solid #94a3b8" : "none",
      color: "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles, props) => ({
      ...styles,
      color: locked(props.data) ? "#334155" : "#FFFFFF",
    }),
    multiValueRemove: (styles, props) => ({
      ...styles,
      display: locked(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
  };

  const Menu = (props: any) => {
    const qa = qaAttrs(qaIdPrefix, "menu");

    return (
      <components.Menu
        {...props}
        innerProps={{
          ...(props.innerProps || {}),
          ...qa,
        }}
      />
    );
  };

  const OptionComponent = (props: any) => {
    const optionId = qaSafe(String(props.data?.value ?? "unknown"));
    const qa = qaAttrs(qaIdPrefix, `option-${optionId}`);

    return (
      <components.Option
        {...props}
        innerProps={{
          ...props.innerProps,
          ...qa,
          "data-option-label": props.data?.label ?? "",
          "data-option-value": props.data?.value ?? "",
        }}
      />
    );
  };

  const MultiValueComponent = (props: any) => {
    const selectedId = qaSafe(String(props.data?.value ?? "unknown"));

    return (
      <div
        {...qaAttrs(qaIdPrefix, `selected-${selectedId}`)}
        data-selected-label={props.data?.label ?? ""}
        data-selected-value={props.data?.value ?? ""}
      >
        <components.MultiValue {...props} />
      </div>
    );
  };

  const MultiValueRemove = (props: MultiValueRemoveProps<SpecialtyOption, true>) =>
    locked(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        captureMenuScroll={true}
        isMulti
        isClearable={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        aria-label="Specialty selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder="Type to search or select..."
        noOptionsMessage={() =>
          loading ? "Loading..." : "No specialties found"
        }
        styles={{
          ...customStyles,
          menu: (base: any) => ({
            ...base,
            zIndex: 999999,
            pointerEvents: "auto",
          }),
          menuList: (base: any) => ({
            ...base,
            maxHeight: 280,
            overflowY: "auto",
            pointerEvents: "auto",
            WebkitOverflowScrolling: "touch",
          }),
        }}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
          MultiValueRemove,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default ListOfSpecialty;
